import {
  Button,
  FormField,
  FormGrid,
  Page,
  Tooltip,
  TooltipProps,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { usersApi } from 'apis/users';
import {
  isNumberValidWithReason,
  numberFormatterE164,
} from 'app/utils/telephonyHelpers';
import { HookFormText } from 'components/hookForm';
import {
  userModalActions,
  ValidatePhoneNumberResponse,
} from 'components/usersModal';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { validateName } from 'utils/formValidator';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useCurrentUser } from 'utils/user';

import { profileActions } from '../../settingsProfile.actions';

import { ProfileAvatarControl } from './components/avatarControl/profileAvatarControl';
import { ProfileFormFieldValues } from './profileForm.types';

export const settingsProfileFormId = 'profile';

export const ProfileForm = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const { user } = useCurrentUser();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const form = useForm<ProfileFormFieldValues>({
    mode: 'all',
    defaultValues: {
      given_name: user.given_name ?? '',
      family_name: user.family_name ?? '',
      name: user.name ?? '',
      email: user.email ?? '',
      phone_number: user.phone_number ?? '',
    },
  });

  const { formState, control, setError } = form;

  const phoneNumber = useWatch({ control, name: 'phone_number' });

  const handlePhoneNumberBlur = useCallback(async () => {
    if (!phoneNumber) return true;

    try {
      const { available, conflicting_id } = (
        await dispatch(
          userModalActions.validation.phoneNumber(
            numberFormatterE164({
              phoneNumber: phoneNumber.replace(/\s/g, ''),
            }),
          ),
        )
      ).payload as ValidatePhoneNumberResponse['data'];

      if (!available && conflicting_id !== user.id) {
        setError('phone_number', {
          type: 'manual',
          message: 'Phone number is already used by another user',
        });
      }
    } catch (error) {}
  }, [dispatch, phoneNumber, setError, user.id]);

  const scimTooltipProps: TooltipProps = useMemo(
    () => ({
      content:
        'You cannot edit this field because the information is coming from another system.',
      disabled: !user.has_scim_enabled,
    }),
    [user],
  );

  const [updateUser] = usersApi.useUpdateUserMutation();

  const handleValidSubmit: SubmitHandler<ProfileFormFieldValues> = async (
    values,
  ) => {
    try {
      const { data } = await updateUser({
        id: user.id,
        patch: {
          updates: {
            display_name: values.name,
            given_name: values.given_name,
            family_name: values.family_name,
            ...(values.phone_number && { phone_number: values.phone_number }),
          },
          deletes: [
            ...(values.phone_number ? [] : (['phone_number'] as const)),
          ],
        },
      }).unwrap();

      dispatch(profileActions.update({ user: data }));

      ampli.settingsProfileFormSubmitted({
        payload: {},
      });

      form.reset(values);

      notify(
        {
          title: 'Success',
          text: `Your profile has been updated.`,
          icon: 'done',
          time: 5000,
        },
        NotificationType.Success,
      );
    } catch (error) {
      notify(
        {
          title: 'Error while saving your profile.',
        },
        NotificationType.Error,
      );
    }
  };

  const pageControls = (
    <>
      <Button
        type="submit"
        form={settingsProfileFormId}
        isLoading={formState.isSubmitting}
        disabled={
          !formState.isDirty || !formState.isValid || formState.isSubmitting
        }
      >
        Save changes
      </Button>
    </>
  );

  return (
    <form
      id={settingsProfileFormId}
      noValidate
      tabIndex={-1}
      onSubmit={form.handleSubmit(handleValidSubmit)}
    >
      <Page.Section
        titleMeta={<ProfileAvatarControl />}
        controls={pageControls}
      >
        <FormGrid>
          <FormField span={isSmallScreen ? 6 : 3}>
            <Tooltip {...scimTooltipProps}>
              <HookFormText
                name="given_name"
                label="First name"
                control={control}
                rules={{
                  required: 'First name is required',
                  validate: validateName,
                }}
                componentProps={{
                  placeholder: 'First name',
                  readOnly: user.has_scim_enabled,
                }}
              />
            </Tooltip>
          </FormField>

          <FormField span={isSmallScreen ? 6 : 3}>
            <Tooltip {...scimTooltipProps}>
              <HookFormText
                name="family_name"
                label="Last name"
                control={control}
                rules={{
                  required: 'Last name is required',
                  validate: validateName,
                }}
                componentProps={{
                  placeholder: 'Last name',
                  readOnly: user.has_scim_enabled,
                }}
              />
            </Tooltip>
          </FormField>

          <FormField span={6}>
            <HookFormText
              name="name"
              label="Display name"
              control={control}
              rules={{
                required: 'Display name is required',
                validate: validateName,
              }}
              componentProps={{
                placeholder: 'Display name',
              }}
            />
          </FormField>

          <FormField span={isSmallScreen ? 6 : 3}>
            <Tooltip {...scimTooltipProps}>
              <HookFormText
                name="phone_number"
                label="Phone number"
                control={control}
                rules={{
                  validate: {
                    isValid: (phoneNumber) =>
                      phoneNumber
                        ? isNumberValidWithReason({ phoneNumber })
                        : true,
                  },
                }}
                componentProps={{
                  type: 'tel',
                  placeholder: 'e.g. +1 205-832-1018',
                  onBlur: handlePhoneNumberBlur,
                  readOnly: user.has_scim_enabled,
                }}
              />
            </Tooltip>
          </FormField>

          <FormField span={isSmallScreen ? 6 : 3}>
            <HookFormText
              name="email"
              label="Email"
              control={control}
              rules={{
                required: 'Email is required',
              }}
              componentProps={{
                type: 'email',
                placeholder: 'john@example.com',
                readOnly: true,
              }}
            />
          </FormField>
        </FormGrid>
      </Page.Section>
    </form>
  );
};
